import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  IdentityWallet,
  SDKOperationRequest,
  SDKResponse,
} from "@extrimian/identity-wallet";
import { AuthInterceptor } from "../handler/auth-interceptor";
import { SystemAccountAuthModel } from "../models/system-account";
import { ApiService } from "./api/api.base.service";
import { AppConfigService } from "src/app/app-config.service";

@Injectable({ providedIn: "root" })
export class IWService {

  handshakeFinished = false;
  baseUrl = "api/account";

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private apiService: ApiService,
    private config: AppConfigService
  ) {}

  /**
   * IMPORTANTE:
   * Llamar a este init() una sola vez (ej: AppComponent / APP_INITIALIZER)
   */
  async init(): Promise<void> {
    IWSingleton.init({
      dappApiUrl: this.config.settings.DAPP_API_URL,
      iwFrontendUrl: this.config.settings.IW_FRONTEND_URL,
    });
  }

  private setToken(token: string): void {
    localStorage.setItem(AuthInterceptor.tokenName, token);
  }

  async handshake(lang: string) {
    this.handshakeFinished = true;
  }

  async login() {
    const iw = IWSingleton.getInstance().getIW();

    await iw.sdkRequest(async (state) => {
      return this.httpClient
        .post<SDKResponse>(`${IWSingleton.controller}/vc-login`, state)
        .toPromise();
    }, SDKOperationRequest.Login);
  }

  async loginDapp(id?: number): Promise<SystemAccountAuthModel> {
    const user = await this.apiService.post<SystemAccountAuthModel>(
      `${this.baseUrl}/login/${id}`
    );

    if (user) {
      this.setToken(user.token);

      if (user.organizations?.length <= 1) {
        this.router.navigate(["/"]);
      }

      return user;
    }

    throw new Error("Login failed");
  }

  async logout() {
    await IWSingleton.getInstance().getIW().logout();
  }

  async extrSign() {
    const iw = IWSingleton.getInstance().getIW();

    const result = await iw.sdkRequest(
      async (state) => {
        return this.httpClient
          .post<SDKResponse>(
            `${IWSingleton.controller}/extr-sign-content`,
            state
          )
          .toPromise();
      },
      SDKOperationRequest.SignContent,
      "Extr"
    );

    return this.httpClient
      .post<SDKResponse>(
        `${IWSingleton.controller}/process-signature`,
        result
      )
      .toPromise();
  }

  async getIW(): Promise<IdentityWallet> {
    return IWSingleton.getInstance().getIW();
  }

  async sign() {
    const iw = IWSingleton.getInstance().getIW();

    return iw.sdkRequest(async (state) => {
      return this.httpClient
        .post<SDKResponse>(
          `${IWSingleton.controller}/sign-content`,
          state
        )
        .toPromise();
    }, SDKOperationRequest.SignContent);
  }

  async addAssertionMethod(userByOrganizationId: number) {
    const iw = IWSingleton.getInstance().getIW();

    const result = await iw.sdkRequest(
      async (state) => {
        return this.httpClient
          .post<SDKResponse>(
            `${IWSingleton.controller}/add-assertion-method`,
            {
              state,
              userByOrganizationId,
            }
          )
          .toPromise();
      },
      SDKOperationRequest.SignContent,
      "Extr"
    );

    await this.httpClient
      .post(
        `${IWSingleton.controller}/process-add-assertion-method`,
        {
          content: result.encryptedContent,
          userByOrganizationId,
        }
      )
      .toPromise();
  }
}

class IWSingleton {

  private static instance: IWSingleton;
  private static config: {
    dappApiUrl: string;
    iwFrontendUrl: string;
  };

  static controller: string;

  private iw: IdentityWallet;

  static init(config: { dappApiUrl: string; iwFrontendUrl: string }) {
    this.config = config;
    this.controller = `${config.dappApiUrl}/identitywallet`;
  }

  private constructor() {
    if (!IWSingleton.config) {
      throw new Error("IWSingleton not initialized");
    }

    this.iw = new IdentityWallet({
      container: "identity-wallet",
      url: IWSingleton.config.iwFrontendUrl,
      sdkProcessMessageEndpoint: `${IWSingleton.controller}/processIWMessage`,
      withCredential: false,
    });
  }

  static getInstance(): IWSingleton {
    if (!this.instance) {
      this.instance = new IWSingleton();
    }
    return this.instance;
  }

  getIW(): IdentityWallet {
    return this.iw;
  }
}

